import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import InfiniteScroll from "react-infinite-scroll-component";
import { Col, Row } from "reactstrap";

import "@/assets/scss/modern-history.css";
import { AvatarIcon } from "@/components/Common/AvatarIcon";
import { DatePickerField } from "@/components/Common/Inputs";
import MultipleFileRenderer from "@/components/Common/MultipleFileRenderer";
import Section from "@/components/Common/Section";
import { formatDate, formatTime } from "@/helpers/date_helper";

import useInfiniteScroll from "../Hooks/useInfiniteScrollHook";

const HistoryList = ({ transactionId, useLogsQuery, idKey = "transactionId", queryArg = {}, title = "History" }) => {
  const { pageDetails, searchTerm, setSearchTerm, handleKeyDown, fetchNext } = useInfiniteScroll();
  const defaultValues = {
    dateTo: new Date().toISOString(),
    dateFrom: null,
  };

  const { control, reset, watch } = useForm({ defaultValues });

  const dateFrom = useWatch({ control, name: "dateFrom" });
  const dateTo = useWatch({ control, name: "dateTo" });

  const payload = { ...pageDetails, dateFrom, dateTo, [idKey]: transactionId, ...queryArg };
  const { data, isFetching } = useLogsQuery(payload, { refetchOnMountOrArgChange: true });

  const items = data?.items ?? [];
  const totalRecords = data?.totalRecords ?? 0;
  const hasMore = items.length < totalRecords;

  useEffect(() => {
    reset(defaultValues);
  }, []);

  return (
    <div className="pa-wrap">
      <Section title={title} />
      <Row className="mt-10">
        <Col lg={4}>
          <div className="pa-datepicker">
            <DatePickerField
              control={control}
              name="dateFrom"
              label="Date From"
              placeholder="Date From..."
              options={{ maxDate: new Date() }}
              rules={{
                validate: (value) => {
                  const df = new Date(value);
                  const dtRaw = watch("dateTo");
                  if (!dtRaw) return true;
                  const dt = new Date(dtRaw);
                  if (isNaN(df.getTime()) || isNaN(dt.getTime())) return "Invalid date format";
                  if (df > dt) return "Date From should not be greater than Date To";
                  return true;
                },
              }}
            />
          </div>
        </Col>

        <Col lg={4}>
          <div className="pa-datepicker">
            <DatePickerField control={control} name="dateTo" label="Date To" placeholder="Date To..." options={{ maxDate: new Date() }} />
          </div>
        </Col>

        <Col lg={4}>
          <label className="pa-label">Search</label>
          <div className="pa-search-box">
            <i className="bx bx-search-alt pa-search-icon" />
            <input
              className="pa-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search here..."
            />
          </div>
        </Col>
      </Row>

      <hr className="pa-divider" style={{ marginBottom: "-16px" }} />

      <Row>
        <div id="pa-scrollable" className="pa-scroll-area pa-timeline" style={{ marginLeft: "16px", marginRight: "16px", height: "60vh", overflow: "auto", width: "100%" }}>
          <InfiniteScroll
            dataLength={items.length}
            next={() => fetchNext(hasMore, isFetching)}
            hasMore={hasMore && !isFetching}
            scrollableTarget="pa-scrollable">
            {items?.length ? (
              items.map((logData, index) => (
                <div key={index} className="pa-item">
                  <div className="pa-avatar">
                    <AvatarIcon marginRight="m-0" name={logData.fullName} avatarImg={logData?.avatar} color={logData.badgeColor} />
                  </div>

                  <div
                    className="pa-item-card"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = "0 4px 14px rgba(64,81,137,0.1)";
                      e.currentTarget.style.borderColor = "#c8cdd5";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = "var(--pa-shadow)";
                      e.currentTarget.style.borderColor = "var(--pa-border-card)";
                    }}>
                    <div className="pa-item-header">
                      <div className="pa-item-name-row">
                        <span className="pa-item-name">{logData.fullName}</span>

                        {logData.badgeText && (
                          <span className={`badge bg-${logData.badgeColor}-subtle text-${logData.badgeColor} align-middle`}>{logData.badgeText}</span>
                        )}
                      </div>

                      <span className="pa-ts">
                        <i className="mdi mdi-clock-outline" />
                        {formatDate(logData.logDate)}, {formatTime(logData.logDate)}
                      </span>
                    </div>

                    {logData.title && <small className="pa-item-subtitle">{logData.title}</small>}

                    {logData.description && (
                      <p
                        className="pa-item-desc"
                        dangerouslySetInnerHTML={{
                          __html: logData.description,
                        }}
                      />
                    )}

                    {logData.remarks && (
                      <p
                        className="pa-item-remarks"
                        dangerouslySetInnerHTML={{
                          __html: `"${logData.remarks}"`,
                        }}
                      />
                    )}

                    {logData.files?.length > 0 && (
                      <div className="mt-2">
                        <MultipleFileRenderer files={logData.files} />
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="pa-empty">{isFetching ? "Loading..." : "No record found."}</div>
            )}
          </InfiniteScroll>
        </div>
      </Row>
    </div>
  );
};

export default HistoryList;
